import React, { useEffect, useState } from "react";
import Layout from "../components/layout";
import Deco from "../components/Deco";

export default function ContactPage() {
  const contactEmail = "casey@alvarezbranding.com";
  const contactEndpoint = process.env.REACT_APP_CONTACT_ENDPOINT || "";
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    if (!recaptchaSiteKey) return;

    const existing = document.querySelector(
      'script[src="https://www.google.com/recaptcha/api.js"]'
    );
    if (existing) return;

    const script = document.createElement("script");
    script.src = "https://www.google.com/recaptcha/api.js";
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
  }, [recaptchaSiteKey]);

  const resetRecaptcha = () => {
    if (window.grecaptcha && typeof window.grecaptcha.reset === "function") {
      window.grecaptcha.reset();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitSuccess(false);
    setSubmitError("");

    if (!contactEndpoint) {
      setSubmitError("Contact form is not configured yet. Please email directly.");
      return;
    }

    if (!recaptchaSiteKey) {
      setSubmitError("reCAPTCHA is not configured. Please try again later.");
      return;
    }

    const captchaToken =
      window.grecaptcha && typeof window.grecaptcha.getResponse === "function"
        ? window.grecaptcha.getResponse()
        : "";

    if (!captchaToken) {
      setSubmitError("Please complete the reCAPTCHA check before submitting.");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(contactEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          name,
          email,
          message,
          captchaToken,
          source: "website-contact-form",
        }),
      });

      if (!response.ok) {
        let errorMessage = "Unable to send your message right now. Please try again.";
        try {
          const data = await response.json();
          if (data && data.message) errorMessage = data.message;
        } catch (_err) {
          // Ignore JSON parse errors and keep fallback message.
        }
        throw new Error(errorMessage);
      }

      setName("");
      setEmail("");
      setMessage("");
      setSubmitSuccess(true);
      resetRecaptcha();
    } catch (err) {
      setSubmitError(err.message || "Something went wrong. Please try again.");
      resetRecaptcha();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="deco-wrap">
        <Deco name="one" className="deco-contact deco-contact--left" />
        <Deco name="two" className="deco-contact deco-contact--right" />
      </div>
      <div className="contact-page">
        <div className="contact-page__inner">
          <h1 className="contact-page__title">Let's chat</h1>
          <p className="contact-page__intro">
            I'm not always taking on new projects, but I'd love to hear what
            you're working on. Drop me a note and let's see if it's a good fit
            to collaborate.
          </p>

          <form className="contact-form" onSubmit={handleSubmit}>
            <div className="contact-form__row">
              <div className="contact-form__field">
                <label className="contact-form__label" htmlFor="contact-name">
                  Name
                </label>
                <input
                  id="contact-name"
                  className="contact-form__input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  autoComplete="name"
                />
              </div>

              <div className="contact-form__field">
                <label className="contact-form__label" htmlFor="contact-email">
                  Email
                </label>
                <input
                  id="contact-email"
                  className="contact-form__input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                  required
                />
              </div>
            </div>

            <div className="contact-form__field">
              <label className="contact-form__label" htmlFor="contact-message">
                What are you working on?
              </label>
              <textarea
                id="contact-message"
                className="contact-form__input contact-form__textarea"
                rows={6}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Tell me a bit about your project, timeline, and what you're looking for..."
                required
              />
            </div>

            <div className="contact-form__captcha">
              {recaptchaSiteKey ? (
                <div className="g-recaptcha" data-sitekey={recaptchaSiteKey} />
              ) : (
                <p className="contact-form__status contact-form__status--error">
                  reCAPTCHA is not configured yet.
                </p>
              )}
            </div>

            <div className="contact-form__actions">
              <button
                type="submit"
                className="a-button a-button--primary"
                disabled={isSubmitting || !recaptchaSiteKey}
              >
                {isSubmitting ? "Sending..." : "Send message"}
              </button>
            </div>

            {submitSuccess ? (
              <p className="contact-form__status contact-form__status--success" role="status">
                Thanks, your message was sent successfully.
              </p>
            ) : null}

            {submitError ? (
              <p className="contact-form__status contact-form__status--error" role="alert">
                {submitError}
              </p>
            ) : null}

            <p className="contact-form__alt">
              Or reach out directly at{" "}
              <a href={`mailto:${contactEmail}`}>{contactEmail}</a>
            </p>
          </form>
        </div>
      </div>
    </Layout>
  );
}
